import type { RefObject } from '@mui/x-internals/types';
import { isDeepEqual } from '@mui/x-internals/isDeepEqual';
import {
  gridColumnFieldsSelector,
  gridColumnLookupSelector,
  gridColumnVisibilityModelSelector,
} from '@mui/x-data-grid-pro';
import { unwrapPrivateAPI } from '@mui/x-data-grid-pro/internals';
import type { GridApiPremium, GridPrivateApiPremium } from '../../../models/gridApiPremium';
import type { GridHistoryEventHandler } from '../history/gridHistoryInterfaces';
import type {
  GridComputedColumnSnapshot,
  GridComputedColumnsInternalCache,
  GridComputedColumnsModel,
} from './gridComputedColumnsInterfaces';
import { gridComputedColumnsSelector } from './gridComputedColumnsSelectors';

export interface GridComputedColumnsHistoryData {
  previousModel: GridComputedColumnsModel;
  nextModel: GridComputedColumnsModel;
  /**
   * The columns the change removed (in `previousModel`, not in `nextModel`), restored by
   * an undo. Captured when the change is stored and refreshed each time a redo removes
   * them again, so a column moved after an undo comes back where the user left it.
   */
  removedColumns: GridComputedColumnSnapshot[];
  /**
   * The columns the change added (in `nextModel`, not in `previousModel`), restored by a
   * redo. They do not exist yet when the change is stored: captured when an undo removes
   * them.
   */
  addedColumns: GridComputedColumnSnapshot[];
}

const getFields = (model: GridComputedColumnsModel): Set<string> =>
  new Set(model.map((definition) => definition.field));

/**
 * The private API carries the caches. The default handlers already receive the private
 * ref (typed as the public one); a handler created from a public `apiRef` unwraps it.
 */
const getPrivateApi = (apiRef: RefObject<GridApiPremium>): GridPrivateApiPremium =>
  unwrapPrivateAPI<GridPrivateApiPremium, GridApiPremium>(apiRef.current) ??
  (apiRef.current as unknown as GridPrivateApiPremium);

/**
 * Drops the echo an undo/redo expects from a controlled parent, with the `columnsChange`
 * listener waiting for it.
 */
const clearHistoryEcho = (cache: GridComputedColumnsInternalCache) => {
  cache.historyEcho?.unsubscribe();
  cache.historyEcho = null;
};

/**
 * Snapshots the computed columns of `from` that `to` does not define, as they stand in
 * the columns state now. Definitions the grid never injected (a field taken by a data
 * column, or an unavailable feature) have no column to capture.
 */
function captureColumns(
  apiRef: RefObject<GridApiPremium>,
  from: GridComputedColumnsModel,
  to: GridComputedColumnsModel,
): GridComputedColumnSnapshot[] {
  const keptFields = getFields(to);
  const lookup = gridColumnLookupSelector(apiRef);
  const orderedFields = gridColumnFieldsSelector(apiRef);
  const visibilityModel = gridColumnVisibilityModelSelector(apiRef);

  const snapshots: GridComputedColumnSnapshot[] = [];
  for (const definition of from) {
    const { field } = definition;
    const column = lookup[field];
    if (keptFields.has(field) || column?.computed !== true) {
      continue;
    }
    snapshots.push({
      field,
      index: orderedFields.indexOf(field),
      width: column.hasBeenResized ? column.width : undefined,
      visible: visibilityModel[field] !== false,
    });
  }
  return snapshots;
}

/**
 * Create the default handler for `computedColumnsChange` events.
 * Every change made from the panel, the column menu or the cells is one step, and so is an
 * API call passed `undoable: true`; the other changes made from code (API, the controlled
 * prop, `restoreState()`) are not, and they invalidate the steps they conflict with, like
 * `updateRows()` does for cell edits. Undo and redo put the whole model back, then restore
 * the position, width and visibility of the columns the step brings back.
 *
 * The handler keeps no state of its own: `store()` runs after the state update, so the
 * previous model of a step is the one `setComputedColumns()` stashed in
 * `caches.computedColumns.previousModel` right before it, the model of an undoable change
 * waits in `caches.computedColumns.userModel`, and the echo an undo/redo expects from
 * a controlled parent waits in `caches.computedColumns.historyEcho`. The history hook may
 * therefore re-create the handler at any time (a handler built before the grid mounted, an
 * inline `historyEventHandlers` map) and stop recording for a while (`historyStackSize` set
 * to 0) without losing the baseline of the next step. At most one echo is pending, with the
 * `columnsChange` listener that restores its columns once it lands: a later undo/redo and a
 * recorded change dispose it, and `useGridComputedColumns` unsubscribes the listener while
 * the effects of the grid are unmounted.
 */
export const createComputedColumnsHistoryHandler = (
  apiRef: RefObject<GridApiPremium>,
): GridHistoryEventHandler<GridComputedColumnsHistoryData> => {
  const getCache = () => getPrivateApi(apiRef).caches.computedColumns;

  /**
   * Re-applies the width and the visibility of the columns the operation brought back
   * (the `hydrateColumns` pass has already inserted them at their index), and scrolls
   * to the first of them.
   */
  const restoreColumns = (columns: GridComputedColumnSnapshot[]) => {
    let firstRestoredField: string | null = null;
    for (const snapshot of columns) {
      const { field, width, visible } = snapshot;
      if (apiRef.current.getColumn(field)?.computed !== true) {
        continue;
      }
      firstRestoredField ??= field;
      if (width !== undefined) {
        apiRef.current.setColumnWidth(field, width);
      }
      if (!visible && gridColumnVisibilityModelSelector(apiRef)[field] !== false) {
        apiRef.current.setColumnVisibility(field, false);
      }
    }
    if (
      firstRestoredField !== null &&
      gridColumnVisibilityModelSelector(apiRef)[firstRestoredField] !== false
    ) {
      apiRef.current.scrollToIndexes({
        colIndex: apiRef.current.getColumnIndex(firstRestoredField),
      });
    }
  };

  /**
   * Puts `model` in place and brings `columns` back where they were. The `hydrateColumns`
   * pass that follows the state update inserts them at the pending index, controlled model
   * or not, so the position needs no second columns update.
   */
  const applyModel = (model: GridComputedColumnsModel, columns: GridComputedColumnSnapshot[]) => {
    const cache = getCache();
    for (const snapshot of columns) {
      cache.pendingColumnIndexes.set(snapshot.field, snapshot.index);
      // The width comes from the snapshot, not from a rejected `restoreState()`.
      cache.pendingColumnDimensions.delete(snapshot.field);
    }

    const modelBefore = gridComputedColumnsSelector(apiRef);
    apiRef.current.setComputedColumns(model);
    const modelAfter = gridComputedColumnsSelector(apiRef);

    if (modelAfter === modelBefore && modelBefore !== model) {
      // Not applied: the model is controlled and the parent has not echoed it yet. With a
      // controlled model, `setComputedColumns()` only calls `onComputedColumnsChange()`;
      // the state follows once the parent echoes the prop, and the columns to restore
      // only exist once that echo is hydrated. The echo may reach the history hook while
      // it still ignores every event as part of this operation (a parent updated from a
      // click is flushed at the first `await`), so `store()` cannot be the one finishing
      // it: the restoration waits for the `columnsChange` that hydrates the echoed model.
      // The operation itself resolves once the model has been requested: a controlled
      // parent is expected to apply the model it is handed, as for every controlled model
      // of the grid. A parent that drops it leaves the step resolved, and `canUndo()` and
      // `canRedo()` optimistic, until the next real change.
      // Only the last operation waits for its echo: the listener of an earlier one is
      // removed here, so a listener never runs once its echo is no longer the one pending.
      clearHistoryEcho(cache);
      const listener = () => {
        const currentModel = gridComputedColumnsSelector(apiRef);
        if (currentModel !== model && !isDeepEqual(currentModel, model)) {
          return;
        }
        clearHistoryEcho(cache);
        restoreColumns(columns);
      };
      cache.historyEcho = {
        model,
        columns,
        listener,
        unsubscribe: apiRef.current.subscribeEvent('columnsChange', listener),
      };
      return;
    }
    clearHistoryEcho(cache);
    restoreColumns(columns);
  };

  return {
    store: (model: GridComputedColumnsModel) => {
      const cache = getCache();
      const previousModel = cache.previousModel ?? [];
      // A model equal to the previous one is not a step: a new but identical array reaches
      // here from an inline `computedColumns` prop on every parent render, from an add of a
      // field the dedupe dropped, or from an update that changed nothing.
      if (previousModel === model || isDeepEqual(previousModel, model)) {
        return null;
      }

      if (cache.historyEcho !== null) {
        // The echo is the operation itself, not a new step. Any other model means the
        // parent did not echo the operation (or normalized it): a step of its own.
        const { model: expectedModel } = cache.historyEcho;
        if (model === expectedModel || isDeepEqual(model, expectedModel)) {
          return null;
        }
        clearHistoryEcho(cache);
      }

      // Set by `setComputedColumns()` for an undoable change (or its controlled echo) only,
      // so any other change from code records nothing. `validate()` drops the steps it
      // conflicts with.
      if (cache.userModel === null) {
        return null;
      }

      return {
        previousModel,
        nextModel: model,
        removedColumns: captureColumns(apiRef, previousModel, model),
        addedColumns: [],
      };
    },

    validate: (data: GridComputedColumnsHistoryData, operation: 'undo' | 'redo') => {
      const cache = getCache();
      // An undo/redo a controlled parent has not echoed yet stands for the current model.
      const currentModel = cache.historyEcho?.model ?? gridComputedColumnsSelector(apiRef);
      const expectedModel = operation === 'undo' ? data.nextModel : data.previousModel;
      return currentModel === expectedModel || isDeepEqual(currentModel, expectedModel);
    },

    undo: (data: GridComputedColumnsHistoryData) => {
      data.addedColumns = captureColumns(apiRef, data.nextModel, data.previousModel);
      applyModel(data.previousModel, data.removedColumns);
    },

    redo: (data: GridComputedColumnsHistoryData) => {
      data.removedColumns = captureColumns(apiRef, data.previousModel, data.nextModel);
      applyModel(data.nextModel, data.addedColumns);
    },
  };
};
